import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, Loading, EmptyState, StatCard } from "@/components/common";
import { CompareEditorDialog } from "@/components/CompareMemberships";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Crown, Users, UserCheck, Mail, Send, Trash2, Save, Megaphone, KeyRound, Plus, Search, Table2, Eye } from "lucide-react";
import { fmtDate } from "@/lib/helpers";

const MEMBERSHIP_STAGES = ["membership_welcome", "membership_renewal", "membership_cancellation"];
const PLACEHOLDERS = ["{{name}}", "{{portal_link}}", "{{platform}}"];

export default function Members() {
  const [members, setMembers] = useState(null);
  const [compose, setCompose] = useState(null); // { email } | { all: true }
  const [pwdMember, setPwdMember] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => { const { data } = await api.get("/members"); setMembers(data); };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const m = members || [];
    return { total: m.length, active: m.filter((x) => x.status === "active").length };
  }, [members]);

  const setStatus = async (email, status) => {
    try {
      await api.put("/members/status", { email, status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const viewAs = async (email) => {
    const tab = window.open("about:blank", "_blank");
    try {
      await api.post(`/members/${encodeURIComponent(email)}/view-as`);
      if (tab) tab.location.href = "/members"; else window.location.href = "/members";
    } catch (e) { tab?.close(); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resendPortal = async (email) => {
    try {
      const { data } = await api.post("/members/resend-portal", { email });
      toast[data.sent ? "success" : "error"](data.sent ? `Portal link emailed to ${email}` : "Email could not be sent (email not configured)");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from members? Their free course access will end.`)) return;
    try {
      await api.delete(`/members/${encodeURIComponent(email)}`);
      toast.success("Member removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!members) return <Loading />;

  return (
    <div>
      {compareOpen && <CompareEditorDialog onClose={() => setCompareOpen(false)} />}
      <PageHeader title="Members" subtitle="Everyone on a monthly membership — manage access and send them emails." testid="members-header">
        <Button variant="outline" onClick={() => setCompareOpen(true)} data-testid="edit-compare-button">
          <Table2 className="h-4 w-4 mr-1.5" />Comparison table
        </Button>
        <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="add-member-button">
          <Plus className="h-4 w-4 mr-1.5" />Add member
        </Button>
        <Button onClick={() => setCompose({ all: true })} data-testid="email-all-members-button" disabled={stats.active === 0}>
          <Megaphone className="h-4 w-4 mr-1.5" />Email all members
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <StatCard label="Total Members" value={stats.total} icon={Users} testid="members-total" />
        <StatCard label="Active Members" value={stats.active} icon={UserCheck} accent="text-emerald-600" testid="members-active" />
      </div>

      {members.length === 0 ? (
        <EmptyState message="No memberships yet. When someone buys a monthly plan they'll appear here." icon={Crown} />
      ) : (
        <Card>
          <div className="p-4 border-b border-border">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or plan..." className="pl-9" data-testid="search-members-input" />
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead><TableHead>Plan</TableHead><TableHead>Seats</TableHead><TableHead>Joined</TableHead>
                <TableHead>Last payment</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.filter((m) => { const s = q.trim().toLowerCase(); return !s || (m.name||"").toLowerCase().includes(s) || (m.email||"").toLowerCase().includes(s) || (m.plan||"").toLowerCase().includes(s); }).map((m) => (
                <TableRow key={m.email} data-testid={`member-row-${m.email}`}>
                  <TableCell>
                    <p className="font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </TableCell>
                  <TableCell className="text-sm">{m.plan}{m.amount != null && <span className="text-muted-foreground"> · £{m.amount}/mo</span>}{m.manual && <span className="ml-1 text-[11px] text-muted-foreground">(manual)</span>}</TableCell>
                  <TableCell><SeatsCell member={m} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(m.joined)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(m.last_payment)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={m.status === "active"} onCheckedChange={(v) => setStatus(m.email, v ? "active" : "inactive")} data-testid={`member-status-toggle-${m.email}`} />
                      <Badge variant="outline" className={m.status === "active" ? "border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10" : "text-muted-foreground"}>{m.status}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" title="View & act as this member" onClick={() => viewAs(m.email)} data-testid={`member-view-as-${m.email}-button`}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Send email" onClick={() => setCompose({ email: m.email })} data-testid={`member-email-${m.email}-button`}><Mail className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title={m.has_password ? "Reset password" : "Set password"} onClick={() => setPwdMember(m)} data-testid={`member-password-${m.email}-button`} className={m.has_password ? "" : "text-amber-600"}><KeyRound className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Resend portal link" onClick={() => resendPortal(m.email)} data-testid={`member-resend-${m.email}-button`}><Send className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" title="Remove member" onClick={() => remove(m.email)} data-testid={`member-remove-${m.email}-button`} className="text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <MembershipTemplates />

      {compose && <ComposeDialog target={compose} activeCount={stats.active} onClose={() => setCompose(null)} />}
      {pwdMember && <MemberPasswordDialog member={pwdMember} onClose={() => setPwdMember(null)} onSaved={load} />}
      {addOpen && <AddMemberDialog onClose={() => setAddOpen(false)} onSaved={load} />}
    </div>
  );
}

function SeatsCell({ member }) {
  const [v, setV] = useState(member.seats ?? 5);
  const save = async () => {
    if (Number(v) === member.seats) return;
    try { await api.put("/members/seats", { email: member.email, seats: Number(v) }); toast.success("Seats updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-1 text-sm">
      <span className="text-muted-foreground">{member.candidate_count ?? 0}/</span>
      <input type="number" min="0" value={v} onChange={(e) => setV(e.target.value)} onBlur={save}
        className="w-14 h-8 rounded border border-border bg-background px-1.5 text-sm" data-testid={`member-seats-${member.email}`} />
    </div>
  );
}

const PLAN_TIERS = [
  ["Childminder", "childminder"],
  ["Silver", "silver"],
  ["Gold", "gold"],
  ["Platinum", "platinum"],
  ["Unlimited", "unlimited"],
];

function AddMemberDialog({ onClose, onSaved }) {
  const [planNames, setPlanNames] = useState([]);
  useEffect(() => { api.get("/membership-plans").then(({ data }) => setPlanNames(data)).catch(() => {}); }, []);
  const tiers = PLAN_TIERS.map(([label, key]) => [label, planNames.find((p) => p.toLowerCase().includes(key)) || `${label} Membership`]);
  const [form, setForm] = useState({ name: "", email: "", plan: "Childminder Membership", amount: "", seats: 5, password: "" });
  useEffect(() => {
    if (planNames.length) setForm((f) => (f.plan === "Childminder Membership" ? { ...f, plan: tiers[0][1] } : f));
  }, [planNames]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setSaving(true);
    try {
      await api.post("/members", {
        name: form.name.trim(), email: form.email.trim(), plan: form.plan.trim() || "Membership",
        amount: form.amount ? Number(form.amount) : null, seats: form.seats ? Number(form.seats) : 5,
        password: form.password || null,
      });
      toast.success("Member added");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Add member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Manually add someone to your membership (e.g. a member who paid outside the shop). They'll count as an active member.</p>
          <div><Label>Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="add-member-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="add-member-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Plan</Label>
              <select value={form.plan} onChange={(e) => set("plan", e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="add-member-plan">
                {tiers.map(([label, value]) => <option key={label} value={value}>{label}</option>)}
              </select>
            </div>
            <div><Label>Amount (£/mo)</Label><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5" data-testid="add-member-amount" /></div>
          </div>
          <div><Label>Seats (candidates allowed)</Label><Input type="number" min="1" value={form.seats} onChange={(e) => set("seats", e.target.value)} className="mt-1.5" data-testid="add-member-seats" /></div>
          <div><Label>Portal password <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Set now or later" className="mt-1.5" data-testid="add-member-password" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="add-member-save-button"><Plus className="h-4 w-4 mr-1.5" />{saving ? "Adding…" : "Add member"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberPasswordDialog({ member, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      await api.post("/members/set-password", { email: member.email, password });
      toast.success(`Password ${member.has_password ? "reset" : "set"} for ${member.email}`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">{member.has_password ? "Reset" : "Set"} member password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Set a portal password for <span className="font-medium text-foreground">{member.name}</span> (<span className="font-mono">{member.email}</span>). Share it with them so they can sign in at the member portal.</p>
          <div><Label>Password</Label><Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="member-password-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="member-password-save-button"><KeyRound className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComposeDialog({ target, activeCount, onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const isAll = !!target.all;

  const send = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Subject and message are required");
    setSending(true);
    try {
      const { data } = await api.post("/members/email", { subject: subject.trim(), message, email: isAll ? null : target.email });
      toast[data.sent ? "success" : "error"](data.sent ? `Email sent to ${data.sent} of ${data.total} recipient(s)` : "No emails sent (email not configured)");
      if (data.sent) onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{isAll ? `Email all active members (${activeCount})` : `Email ${target.email}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A note from the team" className="mt-1.5" data-testid="member-email-subject" /></div>
          <div><Label>Message</Label><Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} placeholder="Write your message…" className="mt-1.5" data-testid="member-email-message" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="member-email-send-button"><Send className="h-4 w-4 mr-1.5" />{sending ? "Sending…" : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembershipTemplates() {
  const [templates, setTemplates] = useState(null);

  const load = async () => {
    const { data } = await api.get("/email-templates");
    setTemplates(data.filter((t) => MEMBERSHIP_STAGES.includes(t.stage)));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-10">
      <div className="mb-4">
        <h2 className="font-heading text-xl font-semibold tracking-tight flex items-center gap-2"><Mail className="h-5 w-5 text-primary" />Membership emails</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Edit the welcome, renewal and cancellation emails for your members.</p>
      </div>
      <div className="mb-4 rounded-lg border border-border bg-accent/30 p-4">
        <p className="text-sm font-medium">Available placeholders</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {PLACEHOLDERS.map((p) => <code key={p} className="text-xs font-mono px-2 py-1 rounded bg-background border border-border">{p}</code>)}
        </div>
      </div>
      {!templates ? <Loading /> : (
        <div className="space-y-5">
          {templates.map((t) => <TemplateCard key={t.stage} tpl={t} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}

function TemplateCard({ tpl, onSaved }) {
  const [form, setForm] = useState(tpl);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/email-templates/${tpl.stage}`, { subject: form.subject, body: form.body, enabled: form.enabled });
      toast.success(`${form.name} saved`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5" data-testid={`membership-template-${tpl.stage}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-primary" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-semibold">{form.name}</h3>
            {form.enabled ? <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-xs">Active</Badge> : <Badge variant="outline" className="text-muted-foreground text-xs">Off</Badge>}
          </div>
        </div>
        <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid={`membership-enabled-${tpl.stage}`} />
      </div>
      <div className="mt-4 space-y-3">
        <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" data-testid={`membership-subject-${tpl.stage}`} /></div>
        <div><Label>Body</Label><Textarea value={form.body} onChange={(e) => set("body", e.target.value)} rows={7} className="mt-1.5 font-mono text-sm" data-testid={`membership-body-${tpl.stage}`} /></div>
      </div>
      <div className="flex justify-end mt-3">
        <Button onClick={save} disabled={saving} data-testid={`membership-save-${tpl.stage}`}><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save"}</Button>
      </div>
    </Card>
  );
}
